// 3rd-party modules
import moment from "moment";
import { Tooltip, message } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// project modules
import Button from "../../shared/button";
import ConfirmationPopup from "../../shared/popup/confirmationPopup";
import DataListView from "../../shared/list/dataListView";
import Loader from "../../shared/loader";
import { apiCall } from "../../../helpers/apiHelper";
import { ListViewType } from "../../shared/list/useListHook";
import { CONSTANTS } from "../../../helpers/defines";
import { DataSourceRequest, FilterDescriptor, PaginationDescriptor, SortDescriptor } from "../../../models/dataSourceRequest";

// apis
import * as AccountSubscriptionApi from '../../../apis/accountSubscriptionApi';

// models
import { Account } from "../../../models/account";
import { AccountSubscription } from "../../../models/accountSubscription";
import { Text } from "../../shared/text";
import Badge from "../../shared/badge";

// defines
type Props = {
  account?: Account;
  refresh?: boolean;
  onCancel?: () => void;
  onSave?: (account: Account) => void;
};

export default function AccountSubscriptions({ account, refresh, onCancel, onSave }: Props) {
  const [currentAccountSubscription, setCurrentAccountSubscription] = useState<AccountSubscription>(new AccountSubscription());
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [isCancelConfirmationPopupOpen, setIsCancelCConfirmationPopupOpen] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const abortController = new AbortController();

  const navigate = useNavigate();

  useEffect(() => {
    if (reload) {
      setReload(false);
    }
  }, [reload]);

  const getPage = async (conditionList?: FilterDescriptor[], sortList?: SortDescriptor[], pagination?: PaginationDescriptor, abortSignal?: AbortSignal) => {
    const request = { conditionList, sortList, pagination } as DataSourceRequest;

    setLoading(true);
    const response = await apiCall(AccountSubscriptionApi.getAccountSubscriptions(account?.accountId!, request, abortSignal));
    setTotalRecords(response.success ? response.extra?.totalCount || 0 : 0);
    setLoading(false);

    const data = response.success ?
      AccountSubscription.toArrayOfClass(response.data?.value || []).map((item: AccountSubscription) => {
        let interval = "";

        if (item.currentPeriodStart && item.currentPeriodEnd) {
          const start = moment(item.currentPeriodStart);
          const end = moment(item.currentPeriodEnd);

          if (end.diff(start, "years") > 0) {
            interval = "Annual"
          } else if (end.diff(start, "months") > 0) {
            interval = "Monthly"
          } else if (end.diff(start, "weeks") > 0) {
            interval = "Weekly"
          } else if (end.diff(start, "days") > 0) {
            interval = "Daily"
          }
        }

        // because after canceling a sub, it reloads to faster that stripe webhook update
        if (item.accountSubscriptionId === currentAccountSubscription?.accountSubscriptionId) {
          item.status = "canceled"
        }

        return {
          ...item,
          canceled: !!(moment() > moment(item.currentPeriodEnd) || item.cancelDate),
          interval
        }
      }) :
      [];

    setCurrentAccountSubscription(new AccountSubscription());

    return data;
  };

  const onStripeCustomerPortalClick = async () => {
    setLoading(true);
    const response = await apiCall(AccountSubscriptionApi.getCustomerPortalSession(account?.accountId!, abortController.signal));
    setLoading(false);

    if (response.success) {
      const newWindow = window.open(response.data?.value.url, '_blank');

      newWindow?.focus();
    } else
      message.error(response.error?.value);
  };

  const onCancelClick = (accountSubscription: AccountSubscription) => {
    setCurrentAccountSubscription(accountSubscription);
    setIsCancelCConfirmationPopupOpen(true);
  };

  const handleCancelRequest = async () => {
    setLoading(true);
    const response = await apiCall(AccountSubscriptionApi.cancelSubscription(currentAccountSubscription));
    setLoading(false);

    if (response.success) {
      message.success(`Subscription canceled successfully.`);
      setIsCancelCConfirmationPopupOpen(false);
      setReload(true);
    } else {
      message.error(response.error?.value)
    }
  };

  const onCreateNewSubscriptionClick = () => {
    navigate("/portal/create-new-subscription");
  }

  const BlockItem: React.FC<{ data: any }> = ({ data }) => {
    return (
      <group
        data-direction="column"
        data-contain=""
        data-border=""
        data-radius="10">
        <group data-direction="column" data-contain="" data-gap="5" data-space="20">
          <Text
            data-weight="700"
            data-ellipsis=""
            data-color="main"
          >
            Subscription
          </Text>
          <Text data-wrap="wrap" data-clamp="3" data-line="1.5">
            Kibosh Device ID: {data.kiboshDeviceReference}
          </Text>
          <Text data-wrap="wrap" data-clamp="3" data-line="1.5">
            Status: <Badge color="blue-grey" text={data.status} />
          </Text>
          <Text data-wrap="wrap" data-clamp="3" data-line="1.5">
            Cancelation Date: {data.cancelDate ? moment(data.cancelDate).format(CONSTANTS.DEFAULT_DATETIME_FORMAT) : "-"}
          </Text>
          <Text data-wrap="wrap" data-clamp="3" data-line="1.5">
            # of VPNs: {data.profile && data.profile['ProductMetadata'] && data.profile['ProductMetadata']['VpnCount'] ? data.profile['ProductMetadata']['VpnCount'] : "-"}
          </Text>
          <Text data-wrap="wrap" data-clamp="3" data-line="1.5">
            Interval: {data.interval}
          </Text>
          <Text data-wrap="wrap" data-clamp="3" data-line="1.5">
            {moment(data.currentPeriodStart).format(CONSTANTS.DEFAULT_DATETIME_FORMAT)}{data.currentPeriodEnd && `- ${moment(data.currentPeriodEnd).format(CONSTANTS.DEFAULT_DATETIME_FORMAT)}`}
          </Text>
        </group>
        <group data-gap="5" data-space="10" data-position="bottom" data-align="center" data-border="" data-wrap="no">
          <Tooltip title="Cancel Subscription">
            <Button
              micro
              icon="delete_history"
              disabled={data.cancelDate || undefined}
              onClick={() => onCancelClick(data)} />
          </Tooltip>
        </group>
      </group>
    )
  };

  return (
    <>
      {loading && <Loader />}
      <view
        data-scroll=""
        data-space="40"
        data-gap="30">
        <group data-gap="10" data-direction="column">
          <text data-weight="700" data-text-size="xx-large" data-wrap="wrap" data-color="main-dark">Subscriptions</text>
          <text data-wrap="wrap" data-length="300" data-line="1.5" data-light="">Here you can manage all your subscriptions.</text>
        </group>
        <group data-width="auto" data-position="left" data-align="center" data-gap="10">
          <Tooltip title="Refresh" arrow={false}>
            <group data-width="auto">
              <Button
                secondary
                data-length="50"
                data-height="50"
                data-elevation="6"
                rounded
                icon="refresh"
                onClick={() => setReload(true)}
              />
            </group>
          </Tooltip>
          <separator data-vertical=""></separator>
          <Button
            material
            text="Add/Renew"
            outline
            large
            icon="add"
            onClick={onCreateNewSubscriptionClick}
          />
          <Button
            material
            text="Manage Subscriptions"
            outline
            large
            disabled={totalRecords === 0}
            onClick={() => onStripeCustomerPortalClick()}
          />
        </group>
        <DataListView
          dataPagination={false}
          getPageAsync={getPage}
          keyField="accountSubscriptionId"
          itemComponent={BlockItem}
          lazyLoadPages={true}
          loading={loading}
          reload={reload}
          renderContainer={false}
          totalRecords={totalRecords}
          view={ListViewType.Block}
        />
      </view>
      {isCancelConfirmationPopupOpen && (
        <ConfirmationPopup
          showButton={false}
          requireMath={true}
          message={ "You will lose access to Portal features if you proceed. You will be able to reactivate them by re-subscribing. Are you sure?" }
          positiveButtonText="Cancel Subscription"
          positiveCallback={handleCancelRequest}
          negativeCallback={() => {
            setCurrentAccountSubscription(new AccountSubscription());
            setIsCancelCConfirmationPopupOpen(false);
          }}
        />
      )}
    </>
  );
}
